import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, Users, PieChart as PieIcon, CheckCircle2, Loader2, BarChart3 } from 'lucide-react';
import {
  groupByDate, groupByReviewer, groupByChartType, computeHeadlineMetrics, prettyChartType,
} from './reviewOutcomesAggregate';

/**
 * Day 10 — Review Outcomes Analytics (frontend-only).
 *
 * Purely derives from `items` already fetched from
 * GET /api/participants/charts/review-outcomes. No API calls here.
 * Charts fail safe: if aggregation breaks, fall back to "No data" text.
 */

// Cohesive, accessible color palette (avoid AI-slop purple/violet).
const PALETTE = ['#0f766e', '#0369a1', '#b45309', '#9f1239', '#047857', '#1e40af', '#b91c1c', '#065f46'];

function Metric({ label, value, testId, icon: Icon, hint }) {
  return (
    <div
      className="rounded-lg border bg-card p-3 flex items-start gap-3"
      data-testid={testId}
    >
      {Icon ? <Icon className="w-4 h-4 text-muted-foreground mt-0.5" /> : null}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold leading-tight">{value}</div>
        {hint ? <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div> : null}
      </div>
    </div>
  );
}

function ChartCard({ title, icon: Icon, empty, children, testId, footer }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-1">
        <CardTitle className="text-xs flex items-center gap-1.5 text-muted-foreground font-medium">
          {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        {empty ? (
          <div className="h-[180px] flex items-center justify-center text-xs text-muted-foreground" data-testid={`${testId}-empty`}>
            No data available
          </div>
        ) : (
          <div className="h-[180px] w-full min-w-0">{children}</div>
        )}
        {footer ? <div className="text-[10px] text-muted-foreground mt-1">{footer}</div> : null}
      </CardContent>
    </Card>
  );
}

export function ReviewOutcomesAnalytics({ items, loading, total, cap }) {
  const safeItems = Array.isArray(items) ? items : [];

  // Wrap each aggregation in try/catch for fail-safe rendering
  const byDate = useMemo(() => { try { return groupByDate(safeItems); } catch { return []; } }, [safeItems]);
  const byReviewer = useMemo(() => { try { return groupByReviewer(safeItems); } catch { return []; } }, [safeItems]);
  const byType = useMemo(() => { try { return groupByChartType(safeItems); } catch { return []; } }, [safeItems]);
  const metrics = useMemo(() => { try { return computeHeadlineMetrics(safeItems); } catch { return null; } }, [safeItems]);

  if (loading && safeItems.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="analytics-loading">
        <div className="md:col-span-4 h-[140px] rounded-lg border bg-muted/20 flex items-center justify-center text-xs text-muted-foreground">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading insights…
        </div>
      </div>
    );
  }

  const capNotice = (cap && total && total > cap)
    ? `Showing insights across the ${cap} most recent reviews (of ${total} in scope). Narrow filters to focus.`
    : null;

  return (
    <div className="space-y-3" data-testid="review-outcomes-analytics">
      {/* Headline metric tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric testId="metric-total" label="Reviews in view" value={metrics?.total ?? 0} icon={CheckCircle2} />
        <Metric testId="metric-reviewers" label="Reviewers" value={metrics?.reviewers ?? 0} icon={Users} />
        <Metric testId="metric-participants" label="Participants" value={metrics?.participants ?? 0} icon={Users} />
        <Metric
          testId="metric-avg-turnaround"
          label="Avg mark → review"
          value={metrics?.avgDays != null ? `${metrics.avgDays.toFixed(1)} d` : '—'}
          icon={Activity}
          hint={metrics?.withNote ? `${metrics.withNote} with outcome note (${metrics.withNotePct}%)` : null}
        />
      </div>

      {/* Three charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ChartCard
          title="Reviews over time"
          icon={Activity}
          testId="chart-over-time"
          empty={byDate.length === 0}
          footer={byDate.length ? `${byDate.length} day${byDate.length === 1 ? '' : 's'} in range` : null}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={byDate} margin={{ top: 6, right: 12, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} tickLine={false} axisLine={false} width={28} />
              <Tooltip
                contentStyle={{ fontSize: 11, padding: 6, borderRadius: 6 }}
                formatter={(v) => [v, 'Reviews']}
                labelStyle={{ fontSize: 11 }}
              />
              <Line type="monotone" dataKey="count" stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 2, fill: PALETTE[0] }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Reviews by coordinator (top 10)"
          icon={BarChart3}
          testId="chart-by-reviewer"
          empty={byReviewer.length === 0}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={byReviewer}
              layout="vertical"
              margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
              barCategoryGap="22%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ fontSize: 11, padding: 6, borderRadius: 6 }}
                formatter={(v) => [v, 'Reviews']}
              />
              <Bar dataKey="count" fill={PALETTE[1]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Chart type mix"
          icon={PieIcon}
          testId="chart-by-type"
          empty={byType.length === 0}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Tooltip
                contentStyle={{ fontSize: 11, padding: 6, borderRadius: 6 }}
                formatter={(v, _n, p) => [v, prettyChartType(p.payload.name)]}
              />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{ fontSize: 11 }}
                formatter={(v) => prettyChartType(v)}
              />
              <Pie
                data={byType}
                dataKey="value"
                nameKey="name"
                innerRadius={34}
                outerRadius={58}
                paddingAngle={1}
              >
                {byType.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {capNotice ? (
        <p className="text-[10px] text-muted-foreground" data-testid="analytics-cap-notice">{capNotice}</p>
      ) : null}
    </div>
  );
}

export default ReviewOutcomesAnalytics;
